import { KAELA } from '@somnhold/content';
import { cosA, iatan2, idiv, imax, imin, mulBp, sinA, withinRange } from '../../math/fixed.ts';
import type { BattleState, Enemy, UnsleepingState } from '../../types.ts';
import { damageHero, ev, spawnEnemy } from '../combat.ts';
import { blowOf } from '../kits.ts';
import { at, by, heroesNear, held, holdable, hurt, isTower, nearestHero, said, standing, type Kit } from './kit.ts';

/*
 * Kaela of the Moss, the druid who called the forest (docs/ANTIHEROES.md): thorns shot from afar; roots that grip a
 * hero who stands still; moss that silences the towers nearest her; thorns that burst under the heroes; a pack of
 * wolves; and the Forest Comes — an enormous circle around her that holds every hero, wraps every tower and mends
 * her. From 33 % she is a bear: up close, harder, and she takes less.
 */

// cds: thorns, pack, moss. k: [mending ticks left, mending carry, then for each hero (by its index): ticks it has stood
// still, and where it stood last tick (x, y)].
// tgts: the towers her moss wraps now (the board draws it on them).
const THORNS = 0;
const PACK = 1;
const MOSS = 2;
const MEND = 0;
const MEND_CARRY = 1;
const STILL = 2;

const bear = (e: Enemy): boolean => e.phase >= 2;

/** Her thorn shot at the nearest hero in reach (not as a bear): true while one is in reach (she stands and shoots). */
const shoot = (s: BattleState, e: Enemy): boolean => {
  const h = nearestHero(s, e, KAELA.shot.range);
  if (!h) return false;
  e.facing = iatan2(h.y - e.y, h.x - e.x);
  e.tgtKind = 1;
  e.tgtId = h.id;
  if (e.cd > 0) return true;
  e.cd = e.reload;
  e.anim = 'attack';
  e.animT = 0;
  ev(s, { t: 'beam', x: e.x, y: e.y, tx: h.x, ty: h.y, kind: 'kaelaShot' });
  kaela.strike(s, e, h, blowOf(s, e, e.damage));
  return true;
};

/** The Late Forest: a hero standing still near her long enough is gripped by roots. */
const roots = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const r = KAELA.roots;
  s.heroes.forEach((h, i) => {
    // Where it stood last tick: any step (its own, a shove, a chase) starts the count again.
    const j = STILL + i * 3;
    const near = standing(h) && withinRange(e.x, e.y, h.x, h.y, r.range);
    const same = u.k[j + 1] === h.x && u.k[j + 2] === h.y;
    const still = near && same ? (u.k[j] ?? 0) + 1 : 0;
    u.k[j] = still;
    u.k[j + 1] = h.x;
    u.k[j + 2] = h.y;
    if (still < r.stillTicks || !holdable(s, h)) return;
    u.k[j] = 0;
    h.stun = imax(h.stun, r.ticks);
    held(s, h);
    hurt(s, e, u, h, r.damage, r.maxHpBp, 'poison');
    ev(s, { t: 'boom', x: h.x, y: h.y, r: 700, kind: 'kaelaRoots' });
  });
};

/** Wraps towers in moss until `until` (nothing cuts it), and keeps them for the board. */
const wrap = (s: BattleState, ids: number[], until: number): void => {
  for (const st of s.structures) {
    if (!ids.includes(st.id)) continue;
    st.dozeUntil = imax(st.dozeUntil ?? 0, until);
    const c = at(st);
    ev(s, { t: 'boom', x: c.x, y: c.y, r: 600, kind: 'kaelaMoss' });
  }
};

/** Every so often, the towers nearest her go silent under moss. */
const moss = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const m = KAELA.moss;
  const near = s.structures
    .filter((st) => isTower(st) && st.type !== 'beacon' && withinRange(e.x, e.y, at(st).x, at(st).y, m.range))
    .map((st) => {
      const c = at(st);
      return { id: st.id, d: (c.x - e.x) * (c.x - e.x) + (c.y - e.y) * (c.y - e.y) };
    })
    .sort((a, b) => a.d - b.d || a.id - b.id)
    .slice(0, m.towers)
    .map((x) => x.id);
  u.cds[MOSS] = m.every;
  if (!near.length) return;
  wrap(s, near, s.tick + m.ticks);
  u.tgts = [...new Set([...u.tgts, ...near])];
  said(s, e, u, 'Moss');
};

/** Thorns: marks under the heroes nearest her (two, three from 66 %) that burst a moment later. */
const thorns = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const t = KAELA.thorns;
  const n = e.phase >= 1 ? t.countLater : t.count;
  const near = s.heroes
    .filter((h) => standing(h) && withinRange(e.x, e.y, h.x, h.y, t.range))
    .sort((a, b) => (a.x - e.x) * (a.x - e.x) + (a.y - e.y) * (a.y - e.y) - ((b.x - e.x) * (b.x - e.x) + (b.y - e.y) * (b.y - e.y)) || a.id - b.id)
    .slice(0, n);
  if (!near.length) {
    u.cds[THORNS] = 6;
    return false;
  }
  for (const h of near) {
    s.zones.push({ id: s.nextId++, kind: 'briar', x: h.x, y: h.y, radius: t.radius, cells: [], ticks: t.warn + 12, every: 1, damage: t.damage, slowBp: 0, pid: '', warn: t.warn });
  }
  u.cds[THORNS] = t.every;
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Thorns');
  return false;
};

/** Her marked thorns: counting down, then bursting on whoever stands in them. */
const briars = (s: BattleState, e: Enemy, u: UnsleepingState): void => {
  const t = KAELA.thorns;
  for (const z of s.zones) {
    if (z.kind !== 'briar' || z.warn === undefined || z.warn < 0) continue;
    if (--z.warn > 0) continue;
    for (const h of heroesNear(s, z.x, z.y, z.radius)) hurt(s, e, u, h, t.damage, t.maxHpBp, 'poison');
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'kaelaThorns' });
    z.warn = -1;
    z.ticks = imin(z.ticks, 12);
  }
};

/** The Pack: wolves around a hero in reach (the nearest), hunting it. */
const pack = (s: BattleState, e: Enemy, u: UnsleepingState): boolean => {
  const p = KAELA.pack;
  const h = nearestHero(s, e, p.range);
  if (!h) {
    u.cds[PACK] = 12;
    return false;
  }
  const n = e.phase >= 1 ? p.countLater : p.count;
  for (let i = 0; i < n; i++) {
    const a = idiv(i * 1024, n) + (s.tick % 1024);
    const w = spawnEnemy(s, 'wolf', e.tier >= 2 ? 2 : 1, false, e.sector, h.x + idiv(cosA(a) * p.ring, 10000), h.y + idiv(sinA(a) * p.ring, 10000));
    w.hp = w.maxHp = mulBp(w.maxHp, p.hpBp);
    w.damage = mulBp(w.damage, u.dmgBp);
    w.taunter = h.id;
    w.tauntTicks = p.hunt;
    ev(s, { t: 'spawn', id: w.id });
  }
  u.cds[PACK] = p.every;
  e.anim = 'cast';
  e.animT = 0;
  said(s, e, u, 'Pack', h.x, h.y);
  return false;
};

export const kaela: Kit = {
  init: (s, _e, u) => {
    u.cds = [idiv(KAELA.thorns.every, 2), idiv(KAELA.pack.every, 2), idiv(KAELA.moss.every, 2)];
    u.k = [0, 0, ...s.heroes.flatMap((h) => [0, h.x, h.y])];
  },
  act: (s, e, u) => {
    if (u.cds[THORNS] === 0) thorns(s, e, u);
    if (u.cds[PACK] === 0) pack(s, e, u);
    // A bear walks in and fights up close (as any nightmare); else she stands and shoots whoever is in reach.
    return !bear(e) && shoot(s, e);
  },
  // Always around her: worth it with a hero or a tower inside.
  aim: (s, e, u) => {
    const f = KAELA.forest;
    const any = heroesNear(s, e.x, e.y, f.radius).length > 0 || s.structures.some((st) => isTower(st) && withinRange(e.x, e.y, at(st).x, at(st).y, f.radius));
    if (!any) return false;
    u.spots = [{ x: e.x, y: e.y, r: f.radius }];
    u.castA = e.facing;
    said(s, e, u, 'Forest');
    return true;
  },
  land: (s, e, u) => {
    const f = KAELA.forest;
    for (const h of heroesNear(s, e.x, e.y, f.radius)) {
      hurt(s, e, u, h, f.damage, f.maxHpBp, 'poison');
      // The forest holds them all (her own roots' respite doesn't spare them from it).
      if (standing(h) && (h.frenzy ?? 0) <= 0) {
        h.stun = imax(h.stun, f.holdTicks);
        held(s, h);
      }
    }
    const ids = s.structures.filter((st) => isTower(st) && st.type !== 'beacon' && withinRange(e.x, e.y, at(st).x, at(st).y, f.radius)).map((st) => st.id);
    wrap(s, ids, s.tick + f.mossTicks);
    u.tgts = [...new Set([...u.tgts, ...ids])];
    u.k[MEND] = f.healTicks;
    u.k[MEND_CARRY] = 0;
    ev(s, { t: 'boom', x: e.x, y: e.y, r: f.radius, kind: 'kaelaForest' });
  },
  // The bear's hide: she takes less.
  takes: (_s, e, _u, dmg) => (bear(e) ? mulBp(dmg, KAELA.bear.takenBp) : dmg),
  strike: (s, e, h, dmg) => {
    const b = KAELA.bear;
    if (bear(e)) damageHero(s, h, by(h, mulBp(dmg, b.blowBp), b.blowMaxHpBp), e, 'poison', true);
    else damageHero(s, h, by(h, dmg, KAELA.shot.maxHpBp), e, 'poison', true);
  },
  phase: (s, e, u) => {
    if (e.phase !== 2) return;
    e.speed = mulBp(e.speed, KAELA.bear.speedBp);
    e.cd = 0;
    said(s, e, u, 'Bear');
  },
  status: (s, e, u) => {
    roots(s, e, u);
    briars(s, e, u);
    if (u.cds[MOSS] === 0) moss(s, e, u);
    // The moss gone from a tower: no longer hers to draw.
    if (u.tgts.length) u.tgts = u.tgts.filter((id) => s.structures.some((st) => st.id === id && !st.destroyed && (st.dozeUntil ?? 0) > s.tick));
    // The forest mends her a little every tick while it lasts.
    if ((u.k[MEND] ?? 0) > 0) {
      u.k[MEND] = (u.k[MEND] as number) - 1;
      const f = KAELA.forest;
      const carry = (u.k[MEND_CARRY] ?? 0) + mulBp(e.maxHp, f.healBp);
      const got = idiv(carry, f.healTicks);
      u.k[MEND_CARRY] = carry - got * f.healTicks;
      if (got > 0 && e.hp > 0) e.hp = imin(e.maxHp, e.hp + got);
    }
  },
};
